import React from 'react';
import { Award, Lightbulb, Star, Mic, Users } from 'lucide-react';
import { Achievement } from './types';

interface ProfileCardAchievementsProps {
  achievements: Achievement[];
  showAchievements: boolean;
  variant: 'preview' | 'full';
  t: (key: string) => string;
}

const getIconByName = (name?: string): React.ReactNode => {
  const iconMap: Record<string, React.ReactNode> = {
    Award: <Award className="w-5 h-5" />,
    Lightbulb: <Lightbulb className="w-5 h-5" />,
    Star: <Star className="w-5 h-5" />,
    Mic: <Mic className="w-5 h-5" />,
    Users: <Users className="w-5 h-5" />
  };
  return iconMap[name || 'Award'] || <Award className="w-5 h-5" />;
};

const getSmallIconByName = (name?: string): React.ReactNode => {
  const iconMap: Record<string, React.ReactNode> = {
    Award: <Award className="w-3 h-3" />,
    Lightbulb: <Lightbulb className="w-3 h-3" />,
    Star: <Star className="w-3 h-3" />,
    Mic: <Mic className="w-3 h-3" />,
    Users: <Users className="w-3 h-3" />
  };
  return iconMap[name || 'Award'] || <Award className="w-3 h-3" />;
};

const ProfileCardAchievements: React.FC<ProfileCardAchievementsProps> = ({
  achievements,
  showAchievements,
  variant,
  t
}) => {
  if (!showAchievements || !achievements || achievements.length === 0) return null;

  const isPreview = variant === 'preview';

  if (isPreview) {
    const displayedAchievements = achievements.slice(0, 2);
    const remainingCount = achievements.length - 2;

    return (
      <div className="mt-2">
        <h4 className="text-[10px] font-semibold text-foreground mb-1.5 uppercase tracking-wide">{t('Achievements')}</h4>
        <div className="space-y-1">
          {displayedAchievements.map((achievement, index) => (
            <div key={index} className="flex items-center p-1.5 bg-amber-50 rounded-lg border border-amber-200">
              <div className="w-6 h-6 flex-shrink-0 bg-amber-100 rounded-md flex items-center justify-center me-2 text-amber-600">
                {getSmallIconByName(achievement.icon)}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium text-foreground truncate">{achievement.title}</p>
                <p className="text-[9px] text-muted-foreground">{achievement.date}</p>
              </div>
            </div>
          ))}
          {remainingCount > 0 && (
            <p className="text-[9px] text-muted-foreground text-center">
              +{remainingCount} {t('more')}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Full variant
  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">{t('Achievements')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {achievements.map((achievement, index) => (
          <div
            key={index}
            className="flex items-center p-3 bg-amber-50 rounded-lg border border-amber-200"
          >
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center me-3 text-amber-600">
              {getIconByName(achievement.icon)}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{achievement.title}</p>
              <p className="text-xs text-muted-foreground">{achievement.date}</p>
              {achievement.description && (
                <p className="text-xs text-muted-foreground mt-1">{achievement.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileCardAchievements;
